<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs :breadcrumbs="page.breadcrumbs" :class="page.headingImage ? '' : 'no-slider'" />

		<main-content
			:title="page.title || page.header"
			:subtitle="page.subtitle"
			:content="page.content"
			:introduction-content="page.introductionContent"
		/>

		<section class="main-content news-section background-light">
			<div class="row flex-row">
				<div class="flex-row column6">
					<div v-for="item in news.slice(0, 1)" :key="item.ID" class="block-item white">
						<nuxt-link :to="localePath(`/${item.filename}`)">
							<div class="block-item-content">
								<span v-if="page.date" class="event-date">
									{{ $d(new Date(item.date.date.split(' ')[0]), 'short', locale) }}
								</span>
								<h3>{{ item.title || item.header }}</h3>
								<div v-parse-links v-html="item.overviewContent" />
								<span class="button-arrow">
									<font-awesome-icon icon="fa-light fa-arrow-right-long" size="1x" />
								</span>
							</div>
							<picture>
								<source
									v-if="item.overviewImageWebp"
									:srcset="item.overviewImageWebp"
									type="image/webp"
								/>
								<source :srcset="item.overviewImage" />
								<img loading="lazy" :src="item.overviewImage" :alt="item.overviewImageAlt" />
							</picture>
						</nuxt-link>
					</div>
				</div>
				<div class="flex-row column6">
					<div v-for="item in news.slice(1, 5)" :key="item.ID" class="block-item white">
						<nuxt-link :to="localePath(`/${item.filename}`)">
							<div class="block-item-content">
								<span v-if="page.date" class="event-date">
									{{ $d(new Date(item.date.date.split(' ')[0]), 'short', locale) }}
								</span>
								<h3>{{ item.title || item.header }}</h3>
								<div v-parse-links v-html="item.overviewContent" />
								<span class="button-arrow">
									<font-awesome-icon icon="fa-light fa-arrow-right-long" size="1x" />
								</span>
							</div>
							<picture>
								<source
									v-if="item.overviewImageWebp"
									:srcset="item.overviewImageWebp"
									type="image/webp"
								/>
								<source :srcset="item.overviewImage" />
								<img loading="lazy" :src="item.overviewImage" :alt="item.overviewImageAlt" />
							</picture>
						</nuxt-link>
					</div>
				</div>
			</div>
		</section>

		<contentblocks
			v-if="page && page.contentblocks && page.contentblocks.length"
			:contentblocks="page.contentblocks"
			:content-blocks-content="page.sectionContentblocksContent"
			:content-blocks-subtitle="page.sectionContentblocksTitle"
		/>
		<frequently-asked-questions
			v-if="page && page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
		<pre-footer v-if="page?.footerSection?.length" :footer-section="page.footerSection[0]" />
		<newsletter
			v-if="page && page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const { data: news } = await useWebsiteFetch('news', {
	query: { language: locale.value },
	key: `${locale.value}/news`,
});
</script>

<style lang="scss">
.news-section {
	position: relative;
}

.news-section .row-header {
	padding-bottom: 25px;
}

.news-section .flex-row,
.packages-section .flex-row {
	row-gap: 25px;
	justify-content: space-between;
}

.block-item {
	width: 48%;
	position: relative;
	margin: 1%;
}

.column6:first-child .block-item {
	width: 98%;
}

.block-item img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.block-item-content,
.block-item::before {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
}

.block-item-content {
	padding: 40px;
	z-index: 3;
	font-weight: 300;
}

.block-item-content h3,
.block-item-content p,
.block-item-content span {
	transition: all 0.3s ease-in-out;
}

.block-item-content h3 {
	font-size: 22px;
	margin: 0;
}

.column6:first-child .block-item-content h3 {
	font-size: 28px;
	margin: 0 0 20px;
}

.block-item-content a {
	color: #fff;
	font-weight: 500;
}

.block-item-content a i {
	margin: 0 0 0 10px;
}

.block-item p {
	padding: 0;
	margin: -30px 0 0;
	opacity: 0;
}

.block-item:hover p {
	margin: 20px 0;
	opacity: 1;
}

.block-item::before {
	content: '';
	display: block;
	top: 0;
	z-index: 2;
	background: rgb(0 0 0 / 15%);
	transition: all 0.3s ease-in-out;
}

.block-item:hover::before {
	background: rgb(0 0 0 / 30%);
}

.news-section .columns {
	padding: 0;
}

.flex-row {
	row-gap: 0;
}

.news {
	.column12 {
		padding: 40px;
		background: #fff;
		margin: 0 auto 40px;
		box-shadow: 0 0 20px rgb(0 0 0 / 5%);
	}

	.news-block,
	.news-block:hover {
		color: #000;
	}

	.news-block {
		span,
		h3 {
			color: var(--cta-color);
		}

		p,
		h3 {
			margin: 0 0 10px;
		}

		.news-image {
			padding: 0 20px 0 0;
			max-width: 30%;
		}

		.news-content {
			padding: 0 20px;
			max-width: 70%;
		}
	}
}

.event-date {
	color: #fff;
	font-size: 14px;
	margin: 0 0 10px;
	display: inline-block;
	width: 100%;
	letter-spacing: 2px;
}

@media (max-width: 768px) {
	.news {
		.column12 {
			padding: 20px;
		}

		.news-block {
			.news-content {
				max-width: 100%;
			}

			.news-image {
				max-width: 100%;
				padding: 20px;
			}
		}
	}
}
</style>
